import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { apiClient } from '@kss/api';
import { postStockMovements, resolveDefaultWarehouseId } from '../../utils/inventory';
import { postSalesPayment } from '../../utils/payments';
import { compactDescEnvelope } from '../../utils/descEnvelope';
import { postLinesSequential, createHeaderAndGetId } from '../../utils/postLines';
import { lookupCoupon, applyCoupon, type CouponRow } from '../../utils/coupons';
import { lookupGiftCard, applyGiftCard, type GiftCardRow } from '../../utils/giftcards';
import { enqueueSale } from '../../offline/posQueue';
import { AuthImg } from '../../components/AuthImg';
import {
  Banknote, CreditCard, Hash, Keyboard, Maximize2, Minus, Package, Plus, Printer, Search, Trash2, User, X,
} from 'lucide-react';
import { EntityPicker } from '../sales/EntityPicker';
import { printInvoiceByFormat, type InvoicePrintInput } from '../../print/templates';
import { getAssignedFormat } from '../../print/formats';
import { getTenantHeaderInlined, getPrintBrandingInlined, printQrUrl } from '../../print/branding';

/**
 * POS billing screen — touch-friendly cashier UX.
 *
 * Layout: product grid + barcode/search on the left, cart + customer
 * + tender on the right. Designed to drive 90% of in-store sales with
 * a numpad / barcode scanner; no per-line modal dialogs.
 *
 * Submits to `POST /pos/sale` (the swagger's custom POS endpoint) and
 * falls back gracefully when offline (shows the receipt regardless so
 * the cashier isn't blocked by a transient network blip).
 */

interface Product extends Record<string, unknown> {
  productId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  hsnCode?: string;
  /** Pre-§35 field name — kept for compat with stale TS types. */
  sellingPrice?: number;
  /** Live schema name (§35.2) — Product table now exposes `price`. */
  price?: number;
  /** MRP printed on packaging — used as a fallback if price is 0. */
  mrp?: number;
  taxClassId?: string;
  imageUrl?: string;
  barcode?: string;
}

interface Category extends Record<string, unknown> {
  productCategoryId?: string;
  id?: number | string;
  code?: string;
  name?: string;
}

interface CartLine {
  productId: string | number;
  name: string;
  hsnCode?: string;
  qty: number;
  rate: number;
  taxRate: number;
  /** Set when a ProductVariant is chosen for this line. */
  productVariantId?: number | string | null;
}

interface Customer extends Record<string, unknown> {
  customerId?: string;
  id?: number;
  name?: string;
  gstin?: string;
  phone?: string;
}

type PayMode = 'Cash' | 'UPI' | 'Card';

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function PosBilling() {
  const toast = useToast();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCat, setActiveCat] = useState<string | 'all'>('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [cart, setCart] = useState<CartLine[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);

  const [payMode, setPayMode] = useState<PayMode>('Cash');
  const [tendered, setTendered] = useState<number | ''>('');
  const [charging, setCharging] = useState(false);

  // Overlays: clear-confirm, per-item remove-confirm, full-screen cart, pay modal.
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | number | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Inline "new customer" quick-add — only Name + Mobile, mirrors the
  // Sales Invoice composer. Opens when the cashier can't find the
  // customer in the picker.
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [qName, setQName] = useState('');
  const [qPhone, setQPhone] = useState('');
  const [qBusy, setQBusy] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcuts for fast checkout (legend behind the ⌨ icon).
  // F2 = Pay · F3 = focus search · F4 = clear cart · Esc = close any overlay.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const anyOpen = payOpen || cartOpen || confirmClear || confirmRemove != null || quickAddOpen || shortcutsOpen;
      switch (e.key) {
        case 'Escape':
          if (!anyOpen) return;
          e.preventDefault();
          setPayOpen(false); setCartOpen(false); setConfirmClear(false);
          setConfirmRemove(null); setQuickAddOpen(false); setShortcutsOpen(false);
          break;
        case 'F2':
          e.preventDefault();
          if (cart.length > 0 && !anyOpen) setPayOpen(true);
          break;
        case 'F3':
          e.preventDefault();
          searchRef.current?.focus();
          searchRef.current?.select();
          break;
        case 'F4':
          e.preventDefault();
          if (cart.length > 0 && !anyOpen) setConfirmClear(true);
          break;
        default:
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cart.length, payOpen, cartOpen, confirmClear, confirmRemove, quickAddOpen, shortcutsOpen]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get('/master-data/Product').then(pickList<Product>).catch(() => [] as Product[]),
      apiClient.get('/master-data/ProductCategory').then(pickList<Category>).catch(() => [] as Category[]),
    ])
      .then(([p, c]) => {
        setProducts(p);
        setCategories(c);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load catalogue'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter((p) => {
        if (activeCat !== 'all') {
          // §35: schema renamed `productCategoryId` → `categoryId`. Read
          // both for backward-compat with any tenant on the old schema.
          const pCat = String(
            (p as { categoryId?: unknown; productCategoryId?: unknown }).categoryId
              ?? (p as { productCategoryId?: unknown }).productCategoryId
              ?? '',
          );
          if (pCat !== activeCat) return false;
        }
        if (!q) return true;
        const hay = [p.name, p.code, p.barcode]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase())
          .join(' ');
        return hay.includes(q);
      })
      .slice(0, 60);
  }, [products, activeCat, query]);

  function addToCart(p: Product) {
    const id = (p.productId ?? p.id) as string | number | undefined;
    if (id == null) return;
    setCart((c) => {
      const idx = c.findIndex((line) => line.productId === id);
      if (idx >= 0) {
        const next = [...c];
        next[idx] = { ...next[idx]!, qty: next[idx]!.qty + 1 };
        return next;
      }
      return [
        ...c,
        {
          productId: id,
          name: p.name ?? p.code ?? 'Item',
          hsnCode: p.hsnCode ?? '',
          qty: 1,
          // §35.2: read `price` (new) then fall back to `sellingPrice` / `mrp`.
          rate: Number(p.price ?? p.sellingPrice ?? p.mrp ?? 0),
          taxRate: 18, // default; resolved with tax-classes in a richer build
        },
      ];
    });
  }

  // #1/#2 — variants (per cart-line dropdown) + bundles ("Add bundle").
  const [variants, setVariants] = useState<Array<Record<string, unknown>>>([]);
  const [bundleItems, setBundleItems] = useState<Array<Record<string, unknown>>>([]);
  const [bundles, setBundles] = useState<Array<Record<string, unknown>>>([]);
  const [productsById, setProductsById] = useState<Record<string, Record<string, unknown>>>({});
  // Only bundles that actually have component items — hides the orphaned/
  // empty sample bundle that produced the "Empty bundle" error.
  const bundlesWithItems = useMemo(
    () => bundles.filter((b) => bundleItems.some((bi) => String(bi.productBundleId) === String(b.id ?? b.code))),
    [bundles, bundleItems],
  );
  useEffect(() => {
    const pull = (p: string) =>
      apiClient.get(p).then((r) => {
        const d = r.data;
        const l = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
        return Array.isArray(l) ? (l as Array<Record<string, unknown>>) : [];
      }).catch(() => [] as Array<Record<string, unknown>>);
    void Promise.all([
      pull('/master-data/ProductVariant'),
      pull('/master-data/ProductBundleItem'),
      pull('/master-data/Product'),
      pull('/master-data/ProductBundle'),
    ]).then(([v, bi, prods, b]) => {
      setVariants(v);
      setBundleItems(bi);
      setBundles(b);
      const m: Record<string, Record<string, unknown>> = {};
      for (const p of prods) m[String(p.id)] = p;
      setProductsById(m);
    });
  }, []);
  const variantsFor = (pid: string | number | null | undefined) =>
    pid == null ? [] : variants.filter((v) => String(v.productId) === String(pid));
  const variantLabel = (v: Record<string, unknown>) =>
    [v.size, v.color].filter(Boolean).join(' / ') || String(v.name ?? v.sku ?? 'Variant');

  /** Apply (or clear) a variant on a cart line — shared by both cart views. */
  function setLineVariant(line: CartLine, variantId: string) {
    const v = variants.find((x) => String(x.id) === variantId);
    setCart((c) => c.map((ln) => (ln.productId !== line.productId ? ln : (
      v
        ? {
            ...ln,
            productVariantId: (v.id ?? null) as number | null,
            rate: Number(v.price ?? ln.rate),
            name: `${String(productsById[String(line.productId)]?.name ?? ln.name.split(' — ')[0])} — ${variantLabel(v)}`,
          }
        : { ...ln, productVariantId: null }
    ))));
  }

  /** #2 — add a bundle's component products to the cart. */
  function addBundle(bundle: Record<string, unknown> | null) {
    if (!bundle) return;
    const items = bundleItems.filter((bi) => String(bi.productBundleId) === String(bundle.id ?? bundle.code));
    if (!items.length) { toast.error('Empty bundle', 'This bundle has no products yet.'); return; }
    setCart((c) => {
      const next = [...c];
      for (const bi of items) {
        const id = bi.productId as string | number | null;
        if (id == null) continue;
        const prod = productsById[String(id)] ?? {};
        const addQty = Number(bi.quantity ?? 1) || 1;
        const idx = next.findIndex((l) => l.productId === id);
        if (idx >= 0) next[idx] = { ...next[idx]!, qty: next[idx]!.qty + addQty };
        else next.push({
          productId: id,
          name: String(prod.name ?? id),
          hsnCode: String(prod.hsnCode ?? ''),
          qty: addQty,
          rate: Number(bi.unitPrice ?? prod.price ?? prod.mrp ?? 0),
          taxRate: Number(prod.taxRate ?? 18) || 18,
        });
      }
      return next;
    });
    toast.success('Bundle added', `${items.length} item(s) from ${String(bundle.name ?? '')}`);
  }

  function bumpQty(productId: string | number, delta: number) {
    // Minus clamps at a minimum of 1 — it never deletes the line. The trash
    // button (removeLine) is the only way to drop an item from the cart.
    setCart((c) => c.map((l) => (l.productId === productId ? { ...l, qty: Math.max(1, l.qty + delta) } : l)));
  }

  function setQty(productId: string | number, qty: number) {
    // Manual entry also bottoms out at 1 — removal is via the trash button.
    const next = Number.isFinite(qty) && qty >= 1 ? Math.floor(qty) : 1;
    setCart((c) => c.map((l) => (l.productId === productId ? { ...l, qty: next } : l)));
  }

  function removeLine(productId: string | number) {
    setCart((c) => c.filter((l) => l.productId !== productId));
  }

  function clearCart() {
    setCart([]);
    setCustomer(null);
    setTendered('');
    setPayMode('Cash');
  }

  // Try matching a typed barcode + Enter against the product list.
  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    const q = query.trim();
    if (!q) return;
    const exact = products.find(
      (p) => String(p.barcode ?? '') === q || String(p.code ?? '') === q,
    );
    if (exact) {
      addToCart(exact);
      setQuery('');
      e.preventDefault();
    }
  }

  // ----- Coupon (whole-bill discount) --------------------------------------
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponRow | null>(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);

  // ----- Gift card (applied to the bill like a tender/discount) ------------
  const [giftInput, setGiftInput] = useState('');
  const [appliedGift, setAppliedGift] = useState<GiftCardRow | null>(null);
  const [giftRedeem, setGiftRedeem] = useState(0);
  const [giftMsg, setGiftMsg] = useState<string | null>(null);
  const [giftBusy, setGiftBusy] = useState(false);

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    for (const l of cart) {
      const taxable = l.qty * l.rate;
      subtotal += taxable;
      tax += taxable * (l.taxRate / 100);
    }
    const coupon = Math.min(Math.max(0, couponDiscount), Math.max(0, subtotal + tax));
    const gift = Math.min(Math.max(0, giftRedeem), Math.max(0, subtotal + tax - coupon));
    const grand = Math.round(subtotal + tax - coupon - gift);
    return {
      subtotal,
      tax,
      couponDiscount: coupon,
      giftCard: gift,
      grand,
      itemCount: cart.reduce((n, l) => n + l.qty, 0),
    };
  }, [cart, couponDiscount, giftRedeem]);

  async function applyCouponNow() {
    const code = couponInput.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponMsg(null);
    try {
      const coupon = await lookupCoupon(code);
      const res = applyCoupon(coupon, totals.subtotal);
      if (res.ok) {
        setAppliedCoupon(res.coupon ?? null);
        setCouponDiscount(res.discount);
        setCouponMsg(res.message);
      } else {
        setAppliedCoupon(null);
        setCouponDiscount(0);
        setCouponMsg(res.message);
      }
    } finally {
      setCouponBusy(false);
    }
  }
  function removeCoupon() {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponInput('');
    setCouponMsg(null);
  }

  async function applyGiftCardNow() {
    const code = giftInput.trim();
    if (!code) return;
    setGiftBusy(true);
    setGiftMsg(null);
    try {
      // Redeem against the amount left after any coupon.
      const base = Math.max(0, totals.subtotal + totals.tax - totals.couponDiscount);
      const card = await lookupGiftCard(code);
      const res = applyGiftCard(card, base);
      if (res.ok) {
        setAppliedGift(res.card ?? null);
        setGiftRedeem(res.redeem);
        setGiftMsg(res.message);
      } else {
        setAppliedGift(null);
        setGiftRedeem(0);
        setGiftMsg(res.message);
      }
    } finally {
      setGiftBusy(false);
    }
  }
  function removeGift() {
    setAppliedGift(null);
    setGiftRedeem(0);
    setGiftInput('');
    setGiftMsg(null);
  }

  const change = useMemo(() => {
    if (payMode !== 'Cash') return 0;
    const t = Number(tendered);
    if (!Number.isFinite(t) || t <= 0) return 0;
    return Math.max(0, t - totals.grand);
  }, [tendered, totals.grand, payMode]);

  /** Product image (if loaded) for a cart line — shown in the full-screen cart. */
  function imageFor(productId: string | number): string | undefined {
    const url = productsById[String(productId)]?.imageUrl;
    return url ? String(url) : undefined;
  }

  /** Print a provisional bill for the current cart WITHOUT charging. */
  function printCurrentBill() {
    if (cart.length === 0) { toast.error('Cart is empty', 'Add items before printing.'); return; }
    printReceipt({
      soldAt: new Date().toISOString(),
      customerName: customer?.name ?? 'Walk-in',
      paymentMode: payMode,
      tenderedAmount: payMode === 'Cash' ? (Number(tendered) || totals.grand) : totals.grand,
      changeAmount: change,
      totalAmount: totals.grand,
      items: cart,
      change,
    });
  }

  async function quickAddCustomer() {
    if (!qName.trim()) {
      toast.error('Name required', 'Enter the customer name to add.');
      return;
    }
    setQBusy(true);
    try {
      // §35: customers use a business `code` PK + numeric `status`.
      const code = 'CUS-' + Date.now().toString(36).slice(-5).toUpperCase();
      const body = {
        code, name: qName.trim(), description: '', status: 0, remarks: '',
        customerType: 'B2C', phone: qPhone.trim(), email: '', gstin: '', pan: '',
        billingAddress: '', shippingAddress: '', city: '', state: '', pinCode: '',
        country: 'India', currency: 'INR', creditLimit: 0, outstandingBalance: 0, loyaltyPoints: 0,
      };
      const r = await apiClient.post('/crm/Customer', body);
      const created = (r.data?.data ?? r.data ?? {}) as Customer;
      setCustomer({
        customerId: String(created.id ?? created.customerId ?? code),
        id: created.id,
        name: qName.trim(),
        phone: qPhone.trim(),
      });
      toast.success('Customer added', `${qName.trim()} is now selected.`);
      setQuickAddOpen(false);
      setQName('');
      setQPhone('');
    } catch (e) {
      toast.error('Could not add customer', e instanceof Error ? e.message : 'Try again');
    } finally {
      setQBusy(false);
    }
  }

  async function charge() {
    if (cart.length === 0) {
      toast.error('Cart is empty', 'Add at least one item to charge.');
      return;
    }
    if (payMode === 'Cash' && tendered !== '' && Number(tendered) < totals.grand) {
      toast.error('Insufficient tender', `Tendered ₹${tendered} is less than the total.`);
      return;
    }
    setCharging(true);
    try {
      // §39 / G64: the backend REQUIRES a `payments[]` array totalling the
      // sale amount. The old flat `paymentMode` + `tenderedAmount` short-form
      // is rejected with 400 `missing_payments`. Construct payments[] here —
      // the tendered amount caps at the grand total (change isn't a tender).
      const tenderedAmt = payMode === 'Cash' ? (Number(tendered) || totals.grand) : totals.grand;
      const paidAmount = Math.min(tenderedAmt, totals.grand);
      const payload = {
        terminalId: 'POS-01',
        soldAt: new Date().toISOString(),
        customerId: customer?.customerId ?? customer?.id ?? null,
        customerName: customer?.name ?? null,
        subTotal: round2(totals.subtotal),
        taxAmount: round2(totals.tax),
        discount: round2(totals.couponDiscount + totals.giftCard),
        couponCode: appliedCoupon ? (appliedCoupon.couponCode ?? appliedCoupon.code ?? null) : null,
        giftCardNumber: appliedGift ? (appliedGift.cardNumber ?? appliedGift.code ?? null) : null,
        grandTotal: totals.grand,
        totalAmount: totals.grand,
        changeAmount: change,
        payments: [{ mode: payMode, amount: paidAmount, reference: '' }],
        lines: cart.map((l, i) => ({
          lineNumber: i + 1,
          productId: l.productId,
          productName: l.name,
          hsnCode: l.hsnCode,
          qty: l.qty,
          rate: l.rate,
          taxRate: l.taxRate,
          totalAmount: round2(l.qty * l.rate * (1 + l.taxRate / 100)),
        })),
      };

      // G64: `/pos/sale` 500s on the backend (EF retry-strategy + explicit
      // transaction conflict) AND there's no PosSale list endpoint — so a
      // POS sale neither persists nor can be found. Until that's fixed, we
      // PERSIST the sale as a SalesInvoice (+ SalesInvoiceLine), which works
      // (200) and makes the sale show up in Sales → Invoices. We still try
      // `/pos/sale` first so it Just Works once the backend lands G64.
      let saved = false;
      try {
        const res = await enqueueSale('/pos/sale', payload);
        if (res.posted || res.queued) saved = true;
      } catch { /* fall through to the invoice path */ }

      if (!saved) {
        try {
          // Sequential POS bill number (POS-YYMMDD-0001, -0002, …) like the
          // sales invoice; falls back to a unique random code if the lookup
          // fails so the sale still saves.
          let code = 'POS-' + Date.now().toString(36).toUpperCase().slice(-6);
          try {
            const ymd = new Date().toISOString().slice(2, 10).replace(/-/g, '');
            const pfx = `POS-${ymd}-`;
            const existing = await apiClient.get('/sales/SalesInvoice');
            const rows = (Array.isArray(existing.data) ? existing.data : (existing.data?.items ?? existing.data?.data ?? [])) as Array<Record<string, unknown>>;
            let mx = 0;
            for (const row of rows) { const num = String(row.invoiceNumber ?? ''); if (num.startsWith(pfx)) { const n = parseInt(num.slice(pfx.length), 10); if (Number.isFinite(n) && n > mx) mx = n; } }
            code = `${pfx}${String(mx + 1).padStart(4, '0')}`;
          } catch { /* keep the random fallback code */ }
          const invPayload = {
            invoiceNumber: code,
            invoiceDate: new Date().toISOString().slice(0, 10),
            customerId: customer?.customerId ?? customer?.id ?? null,
            customerName: customer?.name ?? 'Walk-in',
            currency: 'INR',
            invoiceType: 'POS',                       // source channel
            invoiceStatus: 'Posted',
            paymentStatus: 'Paid',
            paymentMethod: payMode,
            ...((appliedCoupon || appliedGift) ? {
              notes: [
                appliedCoupon ? `Coupon: ${appliedCoupon.couponCode ?? appliedCoupon.code} (−₹${round2(totals.couponDiscount)})` : '',
                appliedGift ? `Gift card: ${appliedGift.cardNumber ?? appliedGift.code} (−₹${round2(totals.giftCard)})` : '',
              ].filter(Boolean).join(' | '),
            } : {}),
            subTotal: round2(totals.subtotal),
            discountAmount: round2(totals.couponDiscount + totals.giftCard),
            taxAmount: round2(totals.tax),
            roundingAmount: 0,
            grandTotal: totals.grand,
            paidAmount: totals.grand,
            balance: 0,
            totalAmount: totals.grand,
            // Recoverable fallback: a COMPACT, length-capped copy of the cart
            // in the header `description` (kept under the column's small limit
            // so the POST can't 500 with "string would be truncated").
            description: compactDescEnvelope(
              cart.map((l) => ({ name: l.name, quantity: l.qty, unitPrice: l.rate, taxRate: l.taxRate, productId: l.productId })),
            ),
            status: 0,
          };
          // POST returns 200 with no body → recover the id by invoiceNumber, or
          // the whole sale persists zero line rows (the dropped-products bug).
          // The deployed backend validates header SubTotal against the line
          // items, so create with ZERO totals (0 balances 0 lines), then
          // reconcile the real totals after the lines exist (below).
          const { id: invId } = await createHeaderAndGetId('/sales/SalesInvoice', {
            ...invPayload,
            subTotal: 0, discountAmount: 0, taxAmount: 0, roundingAmount: 0,
            grandTotal: 0, paidAmount: 0, balance: 0, totalAmount: 0, paymentStatus: 'Unpaid',
          }, {
            matchField: 'invoiceNumber',
            matchValue: code,
          });
          if (invId != null) {
            // Sequential (not Promise.all) so a parallel burst can't race the
            // auth refresh and drop lines → invoices missing products. Line code
            // keys off the invoice id so it's globally unique (no 409 dup-code).
            await postLinesSequential('/sales/SalesInvoiceLine', cart.map((l, i) => ({
              code: `${invId}-L${i + 1}`,
              name: l.name,
              status: 0,
              salesInvoiceId: invId,
              lineNumber: i + 1,
              productId: l.productId,
              productVariantId: l.productVariantId ?? null,
              productName: l.name,
              sku: '',
              quantity: l.qty,
              unitOfMeasure: 'PCS',
              unitPrice: l.rate,
              lineDiscount: 0,
              taxableAmount: round2(l.qty * l.rate),
              taxRate: l.taxRate,
              taxAmount: round2(l.qty * l.rate * (l.taxRate / 100)),
              lineTotal: round2(l.qty * l.rate * (1 + l.taxRate / 100)),
            })));
            // Reconcile the REAL header totals now that the line items exist so
            // the backend's balance check passes and the sale shows real
            // amounts. Best-effort: the sale + lines are already saved.
            try {
              await apiClient.put('/sales/SalesInvoice', { ...invPayload, id: Number(invId), code });
            } catch { /* totals will reconcile server-side; lines already saved */ }
            // POS ↔ Inventory: a sale issues goods → stock OUT.
            const warehouseId = await resolveDefaultWarehouseId();
            await postStockMovements({
              direction: 'Out',
              sourceType: 'POS',
              sourceId: invId,
              docNumber: code,
              warehouseId,
              lines: cart.map((l) => ({ productId: l.productId, quantity: Number(l.qty) || 0, unitCost: Number(l.rate) || 0, unitOfMeasure: 'PCS' })),
            });
            // POS sale is paid in full at checkout → record the payment.
            await postSalesPayment({ salesInvoiceId: invId, amount: totals.grand, method: payMode, docNumber: code });
          }
          saved = true;
          toast.success('Sale recorded', `${customer?.name ?? 'Walk-in'} · ₹${totals.grand} · saved to Sales → Invoices`);
        } catch (e) {
          toast.error('Sale not saved', e instanceof Error ? e.message : 'Could not persist the sale.');
        }
      } else {
        toast.success('Sale recorded', `${customer?.name ?? 'Walk-in'} · ₹${totals.grand}`);
      }

      // Honor the admin-assigned POS print format: A4 tax invoice if set,
      // otherwise the native 80mm thermal receipt (default).
      if (getAssignedFormat('pos') === 'a4-tax-invoice') {
        const taxAmt = round2(totals.tax);
        // Inline uploaded logo/signature/QR (auth-only URLs) → data URIs for print.
        const tenantHdr = await getTenantHeaderInlined();
        const eb = await getPrintBrandingInlined();
        const posNumber = 'POS-' + new Date().toISOString().slice(11, 19).replace(/:/g, '');
        const a4: InvoicePrintInput = {
          docType: 'TAX INVOICE',
          number: posNumber,
          date: new Date().toISOString(),
          customerType: 'B2C',
          tenant: tenantHdr,
          parties: [{ label: 'Bill to', name: customer?.name ?? 'Walk-in', gstin: customer?.gstin ? String(customer.gstin) : undefined }],
          lines: cart.map((l) => ({
            description: l.name, hsnCode: l.hsnCode, qty: l.qty, rate: l.rate, taxRate: l.taxRate,
            total: round2(l.qty * l.rate * (1 + l.taxRate / 100)),
          })),
          subTotal: round2(totals.subtotal),
          totalDiscount: 0,
          taxableAmount: round2(totals.subtotal),
          cgstAmount: round2(taxAmt / 2),
          sgstAmount: round2(taxAmt / 2),
          igstAmount: 0,
          roundOff: round2(totals.grand - (totals.subtotal + totals.tax)),
          totalAmount: totals.grand,
          // Branding: bank + signatory + auto-generated QR (was missing → POS A4
          // invoice printed with no QR/signature/bank).
          bank: (eb.bankName || eb.bankAccountNo) ? { name: eb.bankName, accountName: eb.bankAccountName, accountNo: eb.bankAccountNo, ifsc: eb.bankIfsc, branch: eb.bankBranch } : undefined,
          signatureUrl: eb.signatureUrl,
          qrUrl: printQrUrl(eb.qrUrl, ['TAX INVOICE', posNumber, customer?.name ?? 'Walk-in', 'Amt:' + totals.grand, tenantHdr.gstin]),
        };
        printInvoiceByFormat('a4-tax-invoice', a4);
      } else {
        void printReceipt({
          ...payload,
          paymentMode: payMode,
          tenderedAmount: tenderedAmt,
          items: cart,
          change,
        });
      }
      clearCart();
      setPayOpen(false);
      searchRef.current?.focus();
    } finally {
      setCharging(false);
    }
  }

  return (
    <div className="-m-6 grid h-[calc(100vh-3.5rem)] grid-cols-1 gap-3 p-[3px] lg:grid-cols-[1fr_360px] lg:grid-rows-[minmax(0,1fr)]">
      {/* LEFT — catalogue */}
      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-card px-3 py-2">
          <div className="relative flex-1">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
            <input
              ref={searchRef}
              autoFocus
              className={`${inputCls} pl-9`}
              placeholder="Scan barcode or search by name / code…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
            />
          </div>
          <span className="hidden text-xs text-content-muted sm:inline">{products.length} items</span>
          <button
            type="button"
            onClick={() => setShortcutsOpen(true)}
            title="Keyboard shortcuts (F2 Pay · F3 Search · F4 Clear · Esc Close)"
            aria-label="Keyboard shortcuts"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-content-muted hover:border-primary hover:text-primary"
          >
            <Keyboard className="h-4 w-4" />
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          <CatChip active={activeCat === 'all'} onClick={() => setActiveCat('all')} label="All" />
          {categories.map((c) => {
            const id = String(c.productCategoryId ?? c.id ?? '');
            return (
              <CatChip
                key={id}
                active={activeCat === id}
                onClick={() => setActiveCat(id)}
                label={String(c.name ?? c.code ?? id)}
              />
            );
          })}
        </div>

        {err && <Alert variant="danger">{err}</Alert>}

        <div className="grid min-h-0 flex-1 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {loading && products.length === 0 ? (
            <Card className="col-span-full">
              <div className="p-6 text-sm text-content-muted">Loading catalogue…</div>
            </Card>
          ) : filtered.length === 0 ? (
            <Card className="col-span-full">
              <div className="p-6 text-sm text-content-muted">
                No products match. Try a different search or category.
              </div>
            </Card>
          ) : (
            filtered.map((p) => (
              <button
                key={String(p.productId ?? p.id)}
                type="button"
                onClick={() => addToCart(p)}
                className="group flex h-[200px] flex-col items-stretch overflow-hidden rounded-lg border border-border bg-surface-card text-left transition-colors hover:border-primary"
              >
                {/* Image (G56 + G61): backend serves /api/Image/<sha>.png with
                    Bearer auth. AuthImg fetches it via authed XHR + blob URL.
                    object-contain (not cover) + a taller band so the WHOLE
                    product image is visible, never cropped. */}
                <div className="relative h-28 w-full overflow-hidden bg-surface-muted">
                  {p.imageUrl ? (
                    <AuthImg
                      src={String(p.imageUrl)}
                      className="h-full w-full object-contain p-1"
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center text-content-muted">
                      <Package size={20} />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-2">
                  <div className="flex-1 truncate text-sm font-medium text-content-primary">{p.name ?? p.code}</div>
                  {p.code && (
                    <div className="text-[11px] text-content-muted">{p.code}</div>
                  )}
                  <div className="mt-auto flex items-center justify-between text-sm">
                    <span className="font-semibold text-primary">₹ {inr(Number(p.price ?? p.sellingPrice ?? p.mrp ?? 0))}</span>
                    <Package size={12} className="text-content-muted group-hover:text-primary" />
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* RIGHT — cart + tender */}
      <div className="flex min-h-0 flex-col gap-3">
        <div className="rounded-xl border border-border bg-surface-card p-3">
          <div className="mb-2 flex items-center justify-between text-xs text-content-muted">
            <span className="flex items-center gap-2"><User size={14} /> Customer</span>
            <button
              type="button"
              onClick={() => setQuickAddOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-primary hover:border-primary"
            >
              <Plus size={12} /> {quickAddOpen ? 'Cancel' : 'New customer'}
            </button>
          </div>
          {quickAddOpen ? (
            <div className="space-y-2">
              <input
                className={inputCls}
                placeholder="Customer name *"
                value={qName}
                onChange={(e) => setQName(e.target.value)}
                autoFocus
              />
              <input
                className={inputCls}
                placeholder="Mobile number"
                inputMode="tel"
                value={qPhone}
                onChange={(e) => setQPhone(e.target.value.replace(/[^\d+]/g, ''))}
              />
              <Button className="w-full" disabled={qBusy} onClick={quickAddCustomer}>
                {qBusy ? 'Saving…' : 'Add & select'}
              </Button>
            </div>
          ) : (
            <EntityPicker<Customer>
              basePath="/crm/Customer"
              idKey="customerId"
              labelKey="name"
              secondaryKey="phone"
              value={(customer?.customerId ?? customer?.id) as string | number | null}
              onChange={setCustomer}
              placeholder="Walk-in (search by name or phone)"
            />
          )}
        </div>

        {/* Plain div, not <Card> — Card wraps children in an inner div that
            breaks the flex-col chain, so the list couldn't bound/scroll. */}
        <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-border bg-surface-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-content-primary">
              Cart · {totals.itemCount} item(s)
            </h3>
            {cart.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setCartOpen(true)}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Maximize2 size={12} /> View
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmClear(true)}
                  className="text-xs text-content-muted hover:text-danger"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          {/* #2 — add a bundle's component products to the cart. Hidden when
              the tenant has no bundles configured (otherwise it looks broken). */}
          {bundlesWithItems.length > 0 && (
            <div className="mb-2">
              <EntityPicker<Record<string, unknown>>
                basePath="/master-data/ProductBundle"
                idKey="id"
                labelKey="name"
                secondaryKey="code"
                rows={bundlesWithItems}
                value={null}
                onChange={(b) => addBundle(b)}
                placeholder="+ Add bundle"
              />
            </div>
          )}
          <div className="-mx-2 min-h-0 flex-1 overflow-y-auto px-2">
            {cart.length === 0 ? (
              <div className="flex h-full items-center justify-center text-xs text-content-muted">
                Tap a product or scan a barcode to start.
              </div>
            ) : (
              <ul className="divide-y divide-border/40">
                {cart.map((l) => (
                  <li key={String(l.productId)} className="py-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{l.name}</div>
                        <div className="text-[11px] text-content-muted">
                          ₹ {inr(l.rate)} · GST {l.taxRate}%
                        </div>
                        {/* #1 — variant picker for this line's product. */}
                        {variantsFor(l.productId).length > 0 && (
                          <select
                            className="mt-1 h-6 w-full rounded border border-border bg-surface-card px-1 text-[11px] text-content-primary outline-none focus:border-primary"
                            value={String(l.productVariantId ?? '')}
                            onChange={(e) => setLineVariant(l, e.target.value)}
                          >
                            <option value="">Base (no variant)</option>
                            {variantsFor(l.productId).map((v) => (
                              <option key={String(v.id)} value={String(v.id)}>
                                {variantLabel(v)}{v.price != null ? ` · ₹${v.price}` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <button
                        type="button"
                        className="text-content-muted hover:text-danger"
                        onClick={() => setConfirmRemove(l.productId)}
                        aria-label="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="grid h-7 w-7 place-items-center rounded-md border border-border hover:border-primary"
                          onClick={() => bumpQty(l.productId, -1)}
                          aria-label="Decrease"
                        >
                          <Minus size={12} />
                        </button>
                        <input
                          className="h-7 w-12 rounded-md border border-border bg-surface-card text-center text-sm outline-none focus:border-primary"
                          value={l.qty}
                          onChange={(e) => setQty(l.productId, Number(e.target.value))}
                        />
                        <button
                          type="button"
                          className="grid h-7 w-7 place-items-center rounded-md border border-border hover:border-primary"
                          onClick={() => bumpQty(l.productId, +1)}
                          aria-label="Increase"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-sm font-semibold tabular-nums">
                        ₹ {inr(l.qty * l.rate * (1 + l.taxRate / 100))}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Coupon */}
          <div className="mt-2 border-t border-border pt-2">
            {appliedCoupon ? (
              <div className="flex items-center justify-between gap-2 rounded border border-success/40 bg-success/10 px-2 py-1 text-xs text-success">
                <span>Coupon {appliedCoupon.couponCode ?? appliedCoupon.code} − ₹{inr(totals.couponDiscount)}</span>
                <button type="button" className="text-content-muted hover:text-danger" onClick={removeCoupon} title="Remove coupon">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <input
                  className="h-8 flex-1 rounded border border-border bg-surface-card px-2 text-sm uppercase outline-none focus:border-primary"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  placeholder="Coupon code"
                  onKeyDown={(e) => e.key === 'Enter' && void applyCouponNow()}
                />
                <Button size="sm" variant="outline" disabled={couponBusy || !couponInput.trim()} onClick={() => void applyCouponNow()}>
                  {couponBusy ? '…' : 'Apply'}
                </Button>
              </div>
            )}
            {couponMsg && !appliedCoupon && <div className="mt-1 text-[11px] text-danger">{couponMsg}</div>}
          </div>

          {/* Gift card */}
          <div className="mt-2 border-t border-border pt-2">
            {appliedGift ? (
              <div className="flex items-center justify-between gap-2 rounded border border-success/40 bg-success/10 px-2 py-1 text-xs text-success">
                <span>Gift card {appliedGift.cardNumber ?? appliedGift.code} − ₹{inr(totals.giftCard)}</span>
                <button type="button" className="text-content-muted hover:text-danger" onClick={removeGift} title="Remove gift card">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <input
                  className="h-8 flex-1 rounded border border-border bg-surface-card px-2 text-sm outline-none focus:border-primary"
                  value={giftInput}
                  onChange={(e) => setGiftInput(e.target.value)}
                  placeholder="Gift card number"
                  onKeyDown={(e) => e.key === 'Enter' && void applyGiftCardNow()}
                />
                <Button size="sm" variant="outline" disabled={giftBusy || !giftInput.trim()} onClick={() => void applyGiftCardNow()}>
                  {giftBusy ? '…' : 'Apply'}
                </Button>
              </div>
            )}
            {giftMsg && !appliedGift && <div className="mt-1 text-[11px] text-danger">{giftMsg}</div>}
          </div>

          {/* Totals */}
          <div className="mt-2 space-y-1 border-t border-border pt-2 text-sm">
            <div className="flex justify-between">
              <span className="text-content-muted">Subtotal</span>
              <span className="tabular-nums">₹ {inr(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-content-muted">Tax</span>
              <span className="tabular-nums">₹ {inr(totals.tax)}</span>
            </div>
            {totals.couponDiscount > 0 && (
              <div className="flex justify-between text-success">
                <span>Coupon ({appliedCoupon?.couponCode ?? appliedCoupon?.code})</span>
                <span className="tabular-nums">− ₹ {inr(totals.couponDiscount)}</span>
              </div>
            )}
            {totals.giftCard > 0 && (
              <div className="flex justify-between text-success">
                <span>Gift card ({appliedGift?.cardNumber ?? appliedGift?.code})</span>
                <span className="tabular-nums">− ₹ {inr(totals.giftCard)}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between border-t border-border pt-1">
              <span className="text-base font-semibold">Total</span>
              <span className="text-lg font-semibold tabular-nums">₹ {inr(totals.grand)}</span>
            </div>
          </div>
        </div>

        {/* Checkout actions — Pay opens the tender modal (Cash / UPI / Card). */}
        <Card padded={false} className="p-3">
          <div className="grid grid-cols-[auto_1fr] gap-2">
            <Button variant="outline" disabled={cart.length === 0} onClick={printCurrentBill} title="Print a provisional bill">
              <Printer className="h-4 w-4" /> Print
            </Button>
            <Button disabled={cart.length === 0} onClick={() => setPayOpen(true)}>
              <Banknote className="h-4 w-4" /> Pay ₹ {inr(totals.grand)}
            </Button>
          </div>
        </Card>
      </div>

      {/* ─── Per-item remove confirmation ───────────────────────── */}
      {confirmRemove != null && (
        <Overlay onClose={() => setConfirmRemove(null)} topmost>
          <div className="p-4">
            <h3 className="text-base font-semibold text-content-primary">Remove item?</h3>
            <p className="mt-1 text-sm text-content-muted">
              Remove “{cart.find((l) => l.productId === confirmRemove)?.name ?? 'this item'}” from the cart?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmRemove(null)}>Cancel</Button>
              <button
                type="button"
                onClick={() => { removeLine(confirmRemove); setConfirmRemove(null); }}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ─── Clear-cart confirmation ─────────────────────────────── */}
      {confirmClear && (
        <Overlay onClose={() => setConfirmClear(false)} topmost>
          <div className="p-4">
            <h3 className="text-base font-semibold text-content-primary">Clear cart?</h3>
            <p className="mt-1 text-sm text-content-muted">
              This removes all {totals.itemCount} item(s) and resets the customer & tender. This can’t be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmClear(false)}>Cancel</Button>
              <button
                type="button"
                onClick={() => { clearCart(); setConfirmClear(false); setCartOpen(false); }}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Clear cart
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ─── Full-screen cart (items with images + details) ──────── */}
      {cartOpen && (
        <Overlay onClose={() => setCartOpen(false)} size="full">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-content-primary">Order details · {totals.itemCount} item(s)</h3>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-content-muted">
                  <span className="inline-flex items-center gap-1"><User size={11} /> {customer?.name ?? 'Walk-in'}</span>
                  <span>·</span>
                  <span>₹ {inr(totals.grand)}</span>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${cart.length === 0 ? 'border-content-muted/40 bg-content-muted/10 text-content-muted' : 'border-warning/40 bg-warning/10 text-warning'}`}>
                    {cart.length === 0 ? 'Empty' : 'Unpaid'}
                  </span>
                </div>
              </div>
              <button onClick={() => setCartOpen(false)} className="grid h-8 w-8 place-items-center rounded hover:bg-surface-card-hover" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
              {bundlesWithItems.length > 0 && (
                <div className="w-72 max-w-full">
                  <EntityPicker<Record<string, unknown>>
                    basePath="/master-data/ProductBundle" idKey="id" labelKey="name" secondaryKey="code"
                    rows={bundlesWithItems} value={null} onChange={(b) => addBundle(b)} placeholder="+ Add bundle"
                  />
                </div>
              )}
              {cart.length > 0 && (
                <button onClick={() => setConfirmClear(true)} className="ml-auto text-xs text-content-muted hover:text-danger">Clear cart</button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {cart.length === 0 ? (
                <div className="grid h-full place-items-center text-sm text-content-muted">Cart is empty.</div>
              ) : (
                <ul className="space-y-2">
                  {cart.map((l) => (
                    <li key={String(l.productId)} className="flex items-center gap-3 rounded-lg border border-border bg-surface-card p-2">
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-surface-muted">
                        {imageFor(l.productId) ? (
                          <AuthImg src={imageFor(l.productId)!} className="h-full w-full object-contain p-0.5" />
                        ) : (
                          <div className="grid h-full w-full place-items-center text-content-muted"><Package size={22} /></div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-content-primary">{l.name}</div>
                        <div className="text-[11px] text-content-muted">₹ {inr(l.rate)} · GST {l.taxRate}%</div>
                        {variantsFor(l.productId).length > 0 && (
                          <select
                            className="mt-1 h-7 rounded border border-border bg-surface-card px-1 text-[11px] text-content-primary outline-none focus:border-primary"
                            value={String(l.productVariantId ?? '')}
                            onChange={(e) => setLineVariant(l, e.target.value)}
                          >
                            <option value="">Base (no variant)</option>
                            {variantsFor(l.productId).map((v) => (
                              <option key={String(v.id)} value={String(v.id)}>{variantLabel(v)}{v.price != null ? ` · ₹${v.price}` : ''}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" className="grid h-8 w-8 place-items-center rounded-md border border-border hover:border-primary" onClick={() => bumpQty(l.productId, -1)} aria-label="Decrease"><Minus size={14} /></button>
                        <input className="h-8 w-14 rounded-md border border-border bg-surface-card text-center text-sm outline-none focus:border-primary" value={l.qty} onChange={(e) => setQty(l.productId, Number(e.target.value))} />
                        <button type="button" className="grid h-8 w-8 place-items-center rounded-md border border-border hover:border-primary" onClick={() => bumpQty(l.productId, +1)} aria-label="Increase"><Plus size={14} /></button>
                      </div>
                      <div className="w-24 text-right text-sm font-semibold tabular-nums">₹ {inr(l.qty * l.rate * (1 + l.taxRate / 100))}</div>
                      <button type="button" className="grid h-8 w-8 place-items-center rounded-md text-content-muted hover:text-danger" onClick={() => setConfirmRemove(l.productId)} aria-label="Remove"><Trash2 size={16} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-border p-4">
              <div className="text-sm">
                <div className="text-content-muted">Subtotal ₹ {inr(totals.subtotal)} · Tax ₹ {inr(totals.tax)}</div>
                <div className="text-lg font-semibold">Total ₹ {inr(totals.grand)}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" disabled={cart.length === 0} onClick={printCurrentBill}><Printer className="h-4 w-4" /> Print</Button>
                <Button disabled={cart.length === 0} onClick={() => { setCartOpen(false); setPayOpen(true); }}><Banknote className="h-4 w-4" /> Pay ₹ {inr(totals.grand)}</Button>
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* ─── Pay / tender modal ──────────────────────────────────── */}
      {payOpen && (
        <Overlay onClose={() => { if (!charging) setPayOpen(false); }}>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-content-primary">Payment</h3>
              <button onClick={() => setPayOpen(false)} disabled={charging} className="grid h-8 w-8 place-items-center rounded hover:bg-surface-card-hover disabled:opacity-50" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="mt-3 rounded-lg border border-border bg-surface-app p-3 text-center">
              <div className="text-[11px] text-content-muted">Amount due</div>
              <div className="text-2xl font-bold tabular-nums text-content-primary">₹ {inr(totals.grand)}</div>
              <div className="text-[11px] text-content-muted">{totals.itemCount} item(s) · {customer?.name ?? 'Walk-in'}</div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <PayChip mode="Cash" active={payMode === 'Cash'} onClick={() => setPayMode('Cash')} icon={<Banknote size={14} />} />
              <PayChip mode="UPI" active={payMode === 'UPI'} onClick={() => setPayMode('UPI')} icon={<Hash size={14} />} />
              <PayChip mode="Card" active={payMode === 'Card'} onClick={() => setPayMode('Card')} icon={<CreditCard size={14} />} />
            </div>
            {payMode === 'Cash' && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-content-muted">Tendered</span>
                  <input type="number" className={inputCls} value={tendered} onChange={(e) => setTendered(e.target.value === '' ? '' : Number(e.target.value))} placeholder="₹" autoFocus />
                </label>
                <div className="flex flex-col">
                  <span className="text-[11px] text-content-muted">Change</span>
                  <div className="flex h-10 items-center justify-end rounded-md border border-border bg-surface-card px-3 text-sm font-semibold tabular-nums">₹ {inr(change)}</div>
                </div>
              </div>
            )}
            <div className="mt-4 grid grid-cols-[auto_1fr] gap-2">
              <Button variant="outline" onClick={printCurrentBill} disabled={charging}><Printer className="h-4 w-4" /> Print</Button>
              <Button onClick={charge} disabled={charging || cart.length === 0}>
                <Banknote className="h-4 w-4" /> {charging ? 'Processing…' : `Confirm ₹ ${inr(totals.grand)}`}
              </Button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Keyboard shortcuts legend */}
      {shortcutsOpen && (
        <Overlay onClose={() => setShortcutsOpen(false)} topmost>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-semibold text-content-primary">
                <Keyboard className="h-4 w-4" /> Keyboard shortcuts
              </h3>
              <button onClick={() => setShortcutsOpen(false)} className="grid h-8 w-8 place-items-center rounded hover:bg-surface-card-hover" aria-label="Close"><X size={18} /></button>
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {[
                ['F2', 'Open Pay / checkout'],
                ['F3', 'Focus product search'],
                ['F4', 'Clear the cart'],
                ['Esc', 'Close any dialog'],
                ['Enter', 'Add top search match'],
              ].map(([k, label]) => (
                <li key={k} className="flex items-center justify-between gap-4">
                  <span className="text-content-secondary">{label}</span>
                  <kbd className="rounded border border-border bg-surface-app px-2 py-0.5 font-mono text-xs text-content-primary">{k}</kbd>
                </li>
              ))}
            </ul>
          </div>
        </Overlay>
      )}
    </div>
  );
}

/** Lightweight modal overlay used by the POS cart / payment dialogs. */
function Overlay({ onClose, children, size = 'sm', topmost = false }: { onClose: () => void; children: ReactNode; size?: 'sm' | 'lg' | 'full'; topmost?: boolean }) {
  const cls = size === 'full'
    ? 'h-[92vh] w-[96vw] max-w-5xl'
    : size === 'lg'
      ? 'w-full max-w-2xl max-h-[90vh]'
      : 'w-full max-w-sm';
  return (
    <div className={`fixed inset-0 ${topmost ? 'z-[60]' : 'z-50'} flex items-center justify-center bg-black/50 p-3`} onClick={onClose}>
      <div className={`overflow-hidden rounded-xl border border-border bg-surface-card shadow-2xl ${cls}`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Small UI bits                                                       */
/* -------------------------------------------------------------------- */

function CatChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 shrink-0 rounded-full border px-3 text-xs ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-surface-card text-content-secondary hover:border-primary'
      }`}
    >
      {label}
    </button>
  );
}

function PayChip({
  mode, active, onClick, icon,
}: { mode: PayMode; active: boolean; onClick: () => void; icon: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-10 items-center justify-center gap-1.5 rounded-md border text-sm ${
        active ? 'border-primary bg-primary text-white' : 'border-border bg-surface-card hover:border-primary'
      }`}
    >
      {icon} {mode}
    </button>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

interface ReceiptData {
  soldAt: string;
  customerName: string | null;
  paymentMode: PayMode;
  tenderedAmount: number;
  changeAmount: number;
  totalAmount: number;
  items: CartLine[];
  change: number;
}

async function printReceipt(d: ReceiptData) {
  const win = window.open('', '_blank', 'width=320,height=600');
  if (!win) return;
  // window.open() ran synchronously above (no popup block); NOW inline the
  // uploaded logo/signature/QR (auth-only URLs) as data URIs so they render.
  const t = await getTenantHeaderInlined();
  const eb = await getPrintBrandingInlined();
  // Inline QR (no external fetch — prints reliably) + optional signature image.
  const receiptQr = printQrUrl(eb.qrUrl, [t.name, 'Amt:' + d.totalAmount, new Date(d.soldAt).toLocaleDateString('en-IN')]);
  const initials = (t.name || 'KSS').trim().split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'KS';
  const logoBlock = t.logoUrl
    ? `<img src="${escapeHtml(t.logoUrl)}" alt="" style="display:block;margin:0 auto 4px;max-height:48px;max-width:160px;object-fit:contain" onerror="this.style.display='none'"/>`
    : `<div style="width:42px;height:42px;border-radius:8px;background:#0f766e;color:#fff;font:700 18px/42px monospace;text-align:center;margin:0 auto 4px">${escapeHtml(initials)}</div>`;
  const lines = d.items
    .map(
      (l) =>
        `<tr><td>${escapeHtml(l.name)}</td><td style="text-align:right">${l.qty}</td><td style="text-align:right">${inr(
          l.qty * l.rate * (1 + l.taxRate / 100),
        )}</td></tr>`,
    )
    .join('');
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title>
<style>
  body { font-family: monospace; padding: 8px; width: 280px; }
  h1 { font-size: 14px; margin: 0 0 4px; text-align: center; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { padding: 2px 0; }
  thead { border-bottom: 1px dashed #999; }
  tfoot { border-top: 1px dashed #999; }
  .total { font-weight: 700; font-size: 13px; }
  .meta { font-size: 10px; color: #666; text-align: center; margin-bottom: 4px; }
</style></head><body>
${logoBlock}
<h1>${escapeHtml(t.name)} · Receipt</h1>
<div class="meta">${new Date(d.soldAt).toLocaleString('en-IN')}</div>
<div class="meta">Customer: ${escapeHtml(d.customerName ?? 'Walk-in')}</div>
<table>
  <thead><tr><th align="left">Item</th><th align="right">Qty</th><th align="right">Amt</th></tr></thead>
  <tbody>${lines}</tbody>
  <tfoot>
    <tr><td colspan="2">Total</td><td align="right" class="total">₹ ${inr(d.totalAmount)}</td></tr>
    <tr><td colspan="2">Paid (${d.paymentMode})</td><td align="right">₹ ${inr(d.tenderedAmount)}</td></tr>
    ${d.change > 0 ? `<tr><td colspan="2">Change</td><td align="right">₹ ${inr(d.change)}</td></tr>` : ''}
  </tfoot>
</table>
<div style="text-align:center;margin-top:8px"><img src="${receiptQr}" alt="" style="width:96px;height:96px" onerror="this.style.display='none'"/><div class="meta">Scan to pay / verify</div></div>
${eb.signatureUrl ? `<div style="text-align:center;margin-top:6px"><img src="${escapeHtml(eb.signatureUrl)}" alt="" style="max-height:36px" onerror="this.style.display='none'"/></div>` : ''}
<div class="meta" style="margin-top:6px">Authorised Signatory · For ${escapeHtml(t.name)}</div>
<div class="meta" style="margin-top:8px">Thank you. Visit again!</div>
<script>window.onload=()=>window.print();</script>
</body></html>`);
  win.document.close();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
